/**
 * Aruba devices store — read-only listing of every device the user's
 * active Aruba key can see.
 *
 * Bulk-edit (rename, group/site reassign) was removed 2026-05-06 because
 * the upstream Aruba write surface isn't plumbed yet — the previous
 * `/aruba/devices/bulk-update/` endpoint never existed on the new shim.
 * Reintroduce stage/apply when the write client lands.
 */
import { create } from "zustand";
import { get as apiGet, ApiError } from "@/api/client";
import { ArubaDeviceListSchema, type ArubaDevice } from "@/api/schemas/aruba";
import { MOCK_ARUBA_DEVICES } from "@/api/fixtures/aruba-devices";

interface State {
  data: ArubaDevice[];
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;
  load: () => Promise<void>;
}

export const useArubaDevices = create<State>((set, get) => ({
  data: [],
  status: "idle",
  error: null,

  load: async () => {
    if (get().status === "loading") return;
    set({ status: "loading", error: null });
    try {
      const raw = await apiGet<unknown>("/aruba/devices/");
      set({ data: ArubaDeviceListSchema.parse(raw), status: "ready" });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      set({
        data: MOCK_ARUBA_DEVICES,
        status: "error",
        error: `${message} — showing fixture data`,
      });
    }
  },
}));
